// Клавиатурата и фокусът на общия потвърждаващ диалог.
//
//   Esc        → отказ (ВИНАГИ с preventDefault — иначе глобалната Esc-навигация
//                на SharedAccountGate изхвърля потребителя навън)
//   Enter      → натиска бутона на фокус; без фокус на бутон — бутона по
//                подразбиране (началния фокус). В поле за писане — нов ред.
//   Ctrl/Cmd+Enter → основното действие (и от полето за причина)
//   ←/→ (↑/↓)  → между бутоните/изборите
//
// Слушателят е на window в capture фазата и спира събитието, затова Enter/Esc
// не изтичат към листа зад диалога (решетката, търсачката, Ctrl+Enter = запис).
// Повторенията на задържан Enter се гълтат — задържаният Ctrl+Enter, който
// отваря въпроса, не бива и да го потвърди.
//
// При няколко отворени диалога действа само най-горният АКТИВЕН — затварящият
// се (leave анимацията) вече не е активен и не яде клавишите на следващия.
import { useEffect, useRef, type RefObject } from "react";
import { isEditableTarget } from "../../../../lib/overlay";
import type { ConfirmInitialFocus } from "./confirmTypes";

export const ACTION_ATTR = "data-confirm-action";
const ACTION_SELECTOR = `[${ACTION_ATTR}]`;
const ARROW_STEP: Record<string, number> = {
  ArrowLeft: -1,
  ArrowUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
};

type Layer = { id: number; isActive: () => boolean };
const layers: Layer[] = [];
let layerSeq = 0;

const isTopActiveLayer = (id: number) => {
  for (let i = layers.length - 1; i >= 0; i--) {
    if (layers[i].isActive()) return layers[i].id === id;
  }
  return false;
};

const actionsOf = (panel: HTMLElement) =>
  [...panel.querySelectorAll<HTMLButtonElement>(ACTION_SELECTOR)].filter((button) => !button.disabled);

// Елементът за началния фокус — той е и „бутонът по подразбиране" за Enter
export const initialFocusTarget = (
  panel: HTMLElement,
  focus: ConfirmInitialFocus,
): HTMLElement | null => {
  if (focus === "field") return panel.querySelector<HTMLElement>("textarea, input");
  const key = typeof focus === "string" ? focus : `choice:${focus.choice}`;
  return (
    panel.querySelector<HTMLElement>(`[${ACTION_ATTR}="${key}"]`) ??
    panel.querySelector<HTMLElement>(`[${ACTION_ATTR}="cancel"]`)
  );
};

type Params = {
  panelRef: RefObject<HTMLElement | null>;
  // false, щом диалогът е затворен/затваря се
  isActive: () => boolean;
  busy: boolean;
  initialFocus: ConfirmInitialFocus;
  // бутонът на Enter, когато фокусът не е на бутон (напр. в тялото)
  defaultAction: Exclude<ConfirmInitialFocus, "field">;
  onCancel: () => void;
  // Ctrl/Cmd+Enter
  onPrimary?: () => void;
};

export const useConfirmKeyboard = (params: Params) => {
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    const id = ++layerSeq;
    layers.push({ id, isActive: () => paramsRef.current.isActive() });

    // Фокусът — след ефектите на antd/rc-dialog (те запомнят кой елемент да
    // получи фокуса обратно при затваряне само ако фокусът още е извън модала)
    const focusTimer = window.setTimeout(() => {
      const panel = paramsRef.current.panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      initialFocusTarget(panel, paramsRef.current.initialFocus)?.focus({ preventScroll: true });
    });

    const onKeyDown = (event: KeyboardEvent) => {
      const current = paramsRef.current;
      const panel = current.panelRef.current;
      if (!panel || !isTopActiveLayer(id)) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const consume = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      if (event.key === "Escape") {
        consume();
        if (!current.busy) current.onCancel();
        return;
      }

      if (event.key === "Enter") {
        if (event.isComposing) return;
        const chord = event.ctrlKey || event.metaKey;
        if (!chord && isEditableTarget(target)) return; // нов ред в причината
        consume();
        if (event.repeat || current.busy) return;
        if (chord) {
          current.onPrimary?.();
          return;
        }
        const focused = target?.closest<HTMLButtonElement>(ACTION_SELECTOR);
        const action =
          focused && panel.contains(focused) ? focused : initialFocusTarget(panel, current.defaultAction);
        if (action instanceof HTMLButtonElement && !action.disabled) action.click();
        return;
      }

      const step = ARROW_STEP[event.key];
      if (step && !event.altKey && !event.ctrlKey && !event.metaKey && !isEditableTarget(target)) {
        const actions = actionsOf(panel);
        if (actions.length === 0) return;
        consume();
        const index = actions.findIndex((button) => button === target?.closest(ACTION_SELECTOR));
        const next =
          index === -1 ? 0 : Math.min(actions.length - 1, Math.max(0, index + step));
        actions[next].focus();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown, true);
      const index = layers.findIndex((layer) => layer.id === id);
      if (index !== -1) layers.splice(index, 1);
    };
  }, []);
};
